import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import FormPersonGenerality from '../generality';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, SubjectData } from 'shell/models/person';
import { transformerPersonToRegisterOffice } from 'shell/components/shared/sipo-person-form/transformer-person-to-register-office';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useSearchParams } from 'react-router-dom';
import { transformerEditPersonFromIdIntestatario } from 'shell/components/shared/sipo-person-form/transformer-edit-person';
import { PersonalDataVM } from 'shell/view-models/personal-data';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { TypeRegister } from 'practice/constants/type-register';
import { RegisterOfficeVM } from 'shell/view-models/register-office-practice-vm';

interface AccordionAccountholderGeneralityProps {
  isOpen: boolean;
  formContext: FormikProps<RegisterOfficeVM>;
  disabled?: boolean;
  setOpenModalNewPerson: (value: boolean, idSipo?: string, idAnpr?: string) => void;
  setIntestatarioIsAnpr: (value: boolean) => void;
  typeRegister?: string;
  yearId?: string;
  progressive?: string;
}

const AccordionAccountholderGeneralityComponent = ({
  isOpen,
  formContext,
  disabled,
  typeRegister,
  setOpenModalNewPerson,
  setIntestatarioIsAnpr,
  yearId,
  progressive,
}: AccordionAccountholderGeneralityProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(isOpen);

  const [queryParams] = useSearchParams();

  // id intestatrio se presente proveniamo da modifica persona
  const idIntestatario = queryParams.get('idIntestatario') || '';
  const idAnpr = queryParams.get('idAnpr') || '';

  const getData = useHttpClient<HttpResponse<SubjectData>, PersonalDataVM>(``, {
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerEditPersonFromIdIntestatario,
  });

  useEffect(() => {
    if (!idIntestatario && !idAnpr) {
      return;
    }

    (
      getData({
        url:
          idIntestatario && idIntestatario !== '0'
            ? `${__PERSON_API_URL__}/persona/pratiche/sipo/soggetto/${idIntestatario}`
            : `${__PERSON_NEW_API_URL__}/persona`,
        method: idIntestatario && idIntestatario !== '0' ? 'GET' : 'POST',
        data:
          idIntestatario && idIntestatario !== '0'
            ? {}
            : {
                criteriRicerca: {
                  idSchedaSoggettoANPR: idAnpr || undefined,
                },
                datiRichiesta: {
                  ricercaNazionale: '1',
                },
              },
      }) as ResponsePromise<PersonalDataVM>
    ).then((res) => {
      const castedRes = res as PersonalDataVM;

      formContext.setFieldValue('accountholderData', castedRes);
    });
  }, [idIntestatario, idAnpr]);

  // ricerca sia su ANPR che DB
  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, RegisterOfficeVM>(
    `${__PERSON_API_URL__}/persona/pratiche/ricerca-intestatario`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
      transformer: transformerPersonToRegisterOffice,
    }
  );

  useEffect(() => {
    if (isOpen) setOpen(false);
    else setOpen(true);
  }, [isOpen]);

  const onChange = useEventCallback(
    (key: keyof PersonalDataVM, value: PersonalDataVM[keyof PersonalDataVM]) => {
      formContext.setFieldValue('accountholderData', {
        ...(formContext.values.accountholderData || {}),
        [key]: value,
      });
    }
  );

  const onChangeFields = useEventCallback((values: PersonalDataVM) => {
    formContext.setFieldValue('accountholderData', {
      ...(formContext.values.accountholderData || {}),
      ...values,
    });
  });

  const onSearch = useEventCallback(() => {
    const hasSearchBirthFilters =
      !!formContext.values.accountholderData?.birthdayChoice &&
      !!formContext.values.accountholderData?.birthday;

    const data = {
      tipoRegistro: typeRegister,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      datiRichiesta: {
        ricercaNazionale: '1', // fixed
      },
      datiNascita: hasSearchBirthFilters
        ? {
            senzaGiorno:
              !formContext.values.accountholderData?.birthdayChoice?.includes('day') &&
              formContext.values.accountholderData?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              !formContext.values.accountholderData?.birthdayChoice?.includes('day') &&
              !formContext.values.accountholderData?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            dataEvento:
              formContext.values.accountholderData?.birthday?.toFormat('yyyy-MM-dd') || undefined,
          }
        : undefined,
      criteriRicerca: {
        codiceFiscale: formContext.values.accountholderData?.fiscalCode || undefined,
        cognome: formContext.values.accountholderData?.surname || undefined,
        nome: formContext.values.accountholderData?.name || undefined,
        senzaCognome: formContext.values.accountholderData?.withoutSurname ? '1' : undefined,
        senzaNome: formContext.values.accountholderData?.withoutName ? '1' : undefined,
      },
    };

    (getPerson({ data }) as ResponsePromise<HttpResponse<ResponseAnprData>, RegisterOfficeVM>).then(
      (response) => {
        const castedResponse = response as RegisterOfficeVM;

        if (typeRegister !== TypeRegister.Emigration) {
          if (castedResponse.accountholderData?.openModalNewPerson) {
            // open modal registrazione nuova persona
            setOpenModalNewPerson(true);
            return;
          }

          if (castedResponse.accountholderData?.residenceOrigin?.irreperibile) {
            // open modal registrazione nuova persona anche se è irreperibile
            setOpenModalNewPerson(
              true,
              castedResponse.accountholderData?.idSchedaSoggetto,
              castedResponse.accountholderData?.generality?.anprIdentifier
            );
          } else {
            if (castedResponse.accountholderData?.isSipo) {
              // open modal registrazione nuova persona
              setOpenModalNewPerson(true, castedResponse.accountholderData?.idSchedaSoggetto);
            }
          }
        }

        formContext.setValues({
          ...(formContext.values || {}),
          accountholderData: castedResponse.accountholderData,
        });

        setIntestatarioIsAnpr(castedResponse.isAire || false);
      }
    );
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls={`panel-register-office-Generalità intestatario-content`}
        id={`panel-register-office-Generalità intestatario-header`}
        expandIcon={<ExpandMore />}
      >
        <Typography>Generalità intestatario</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormPersonGenerality
          onChangeFields={onChangeFields}
          onChange={onChange}
          values={formContext.values.accountholderData || {}}
          onSearch={onSearch}
          onChoicePerson={(castedResponse) => {
            setIntestatarioIsAnpr(castedResponse.isAire || false);
          }}
          disabled={disabled}
          typeRegister={typeRegister}
          yearId={yearId}
          personFilterAccountholderBlur={(fieldName: string) =>
            formContext?.setFieldTouched(fieldName, true)
          }
          progressive={progressive}
          isSearchPersonFilterForAccountHolder
          setOpenModalNewPerson={setOpenModalNewPerson}
          disabledGenerality // generalità per intestatario sempre disabilitate
          errors={formContext.errors.accountholderData as FormikErrors<PersonalDataVM>}
          touched={
            formContext.touched.accountholderData as unknown as FormikTouched<PersonalDataVM>
          }
        />
      </AccordionDetails>
    </Accordion>
  );
};

AccordionAccountholderGeneralityComponent.displayName =
  'Accordion Register Office Person generality component';

const AccordionAccountholderGenerality = memo(AccordionAccountholderGeneralityComponent);

export default AccordionAccountholderGenerality;
